/**
 * BulkReassignDialog — Reassign all members from one coordinator to another
 * Phase 2 Issue 11b
 */

import React, { useState, useEffect } from 'react';
import { ArrowRight, Users, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/services/apiClient';

interface User {
  id: number;
  name: string;
  role?: string;
}

interface Props {
  cboId: number;
  onClose: () => void;
  onComplete?: () => void;
}

const STAFF_ROLES = ['super_admin', 'org_admin', 'ops_admin', 'program_manager', 'manager', 'supervisor', 'coordinator', 'care_coordinator', 'staff', 'chw'];

export default function BulkReassignDialog({ cboId, onClose, onComplete }: Props) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromUserId, setFromUserId] = useState<string>('');
  const [toUserId, setToUserId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ reassigned_count: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get(`/api/cbos/${cboId}/users`)
      .then(resp => {
        const all = Array.isArray(resp.data) ? resp.data : (resp.data?.users || []);
        setUsers(
          all
            .filter((u: any) => STAFF_ROLES.includes((u.user_org_role || u.role || '').toLowerCase()))
            .map((u: any) => ({
              id: u.id,
              name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
              role: u.user_org_role || u.role,
            }))
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cboId]);

  const handleSubmit = async () => {
    if (!fromUserId || !toUserId || fromUserId === toUserId) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const resp = await apiClient.post('/api/members/bulk-reassign-coordinator', {
        from_user_id: Number(fromUserId),
        to_user_id: Number(toUserId),
      }, {
        headers: { 'X-CBO-ID': cboId.toString() },
      });
      setResult(resp.data);
      onComplete?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to reassign');
    } finally {
      setSubmitting(false);
    }
  };

  const fromUser = users.find(u => u.id === Number(fromUserId));
  const toUser = users.find(u => u.id === Number(toUserId));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Bulk Reassign Caseload
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <span className="text-xl">&times;</span>
        </button>
      </div>

      <div className="px-6 py-5 space-y-4">
        {result ? (
          <div className="text-center py-4">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-semibold text-green-700">
              {result.reassigned_count} member{result.reassigned_count !== 1 ? 's' : ''} reassigned
            </p>
            <p className="text-sm text-gray-500 mt-1">
              From {fromUser?.name} → {toUser?.name}
            </p>
            <Button className="mt-4" onClick={onClose}>Close</Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Transfer all members assigned to one coordinator to another. This updates the primary coordinator field on all matching members.
            </p>

            {loading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block uppercase tracking-wider">From Coordinator</label>
                  <Select value={fromUserId} onValueChange={setFromUserId}>
                    <SelectTrigger><SelectValue placeholder="Select current coordinator..." /></SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {u.name} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-center">
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block uppercase tracking-wider">To Coordinator</label>
                  <Select value={toUserId} onValueChange={setToUserId}>
                    <SelectTrigger><SelectValue placeholder="Select new coordinator..." /></SelectTrigger>
                    <SelectContent>
                      {users.filter(u => String(u.id) !== fromUserId).map(u => (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {u.name} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {fromUserId && toUserId && fromUserId !== toUserId && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-amber-800">
                      All members with primary coordinator <Badge variant="outline">{fromUser?.name}</Badge> will be reassigned to <Badge variant="outline">{toUser?.name}</Badge>.
                    </p>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={!fromUserId || !toUserId || fromUserId === toUserId || submitting}
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Reassigning...</> : 'Reassign Caseload'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
